import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight } from 'lucide-react'
import { getReservationList } from '../../lib/api/auth'
import { demoByEmail } from '../../lib/demo'
import { useAuth } from '../../store/authStore'
import { ReservationCard } from '../account/ReservationCard'
import { TicketModal } from '../account/TicketModal'
import type { Locale, ReservationListItem } from '../../lib/api/types'

/**
 * Homepage shortcut: upcoming bookings for a logged-in user, shown right under the hero, using
 * the SAME ticket card as Le mie prenotazioni. Purely additive — renders NOTHING for guests or
 * when there's no upcoming booking, so the homepage is unchanged for everyone else.
 */
export function HomeBookings() {
  const { t, i18n } = useTranslation()
  const locale = (i18n.language as Locale) ?? 'it'
  const { locale: routeLocale = 'it' } = useParams()
  const status = useAuth((s) => s.status)
  const user = useAuth((s) => s.user)
  const demo = demoByEmail(user?.email)
  const [ticket, setTicket] = useState<ReservationListItem | null>(null)

  const { data } = useQuery({
    queryKey: ['reservationList', user?.email],
    queryFn: () => (demo ? Promise.resolve(demo.reservations) : getReservationList()),
    enabled: status === 'authenticated',
  })

  const active = data?.activeList ?? []
  if (status !== 'authenticated' || active.length === 0) return null

  return (
    <section className="pt-12 lg:pt-14">
      <div className="mb-4 flex items-end justify-between gap-3">
        <h2 className="font-display text-xl font-extrabold tracking-tight text-ink-900 sm:text-2xl">
          {active.length === 1 ? t('home.yourBooking') : t('home.yourBookings')}
        </h2>
        <Link to={`/${routeLocale}/bookings`} className="inline-flex flex-none items-center gap-1 text-sm font-semibold text-brand-600 hover:text-brand-800">
          {t('home.seeAll')} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {active.length === 1 ? (
        // single booking → a full-width ticket
        <div className="max-w-2xl">
          <ReservationCard r={active[0]} locale={locale} onOpen={() => setTicket(active[0])} />
        </div>
      ) : (
        // several → wide cards in a horizontal swipe carousel
        <div className="-mx-5 flex snap-x gap-4 overflow-x-auto px-5 pb-1 [scrollbar-width:none] sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden">
          {active.map((r) => (
            <div key={r.code} className="w-[88vw] max-w-[460px] flex-none snap-start sm:w-[440px]">
              <ReservationCard r={r} locale={locale} onOpen={() => setTicket(r)} />
            </div>
          ))}
        </div>
      )}

      {ticket && <TicketModal r={ticket} onClose={() => setTicket(null)} />}
    </section>
  )
}
